import React, { useEffect } from "react";
import { MapPin } from "lucide-react";
import { useCheckoutAddresses } from "../hooks/use-checkout-addresses";
import { useCheckoutStore } from "../store/checkout-store";
import { useTranslation } from "react-i18next";
import { LoadingSpinner } from "@/components/common/loading-spinner";
import { AddressFormInline } from "../components/AddressFormInline";
import { AddressPicker } from "../components/AddressPicker";
import { cn } from "@/lib/utils";

export const DeliveryAddressSection: React.FC = () => {
  const { t } = useTranslation();
  const {
    addresses,
    isLoading,
    selectedDeliveryAddressId,
    setDeliveryAddress,
    openDeleteAddressDialog,
  } = useCheckoutAddresses();
  const {
    isAddingNewAddress,
    isEditingDeliveryAddress,
    setIsAddingNewAddress,
    setIsEditingDeliveryAddress,
  } = useCheckoutStore();

  const selectedAddress = addresses.find(
    (a) => a._id === selectedDeliveryAddressId
  );
  const isFormOpen = isAddingNewAddress || isEditingDeliveryAddress;

  useEffect(() => {
    if (!isLoading && addresses.length === 0 && !isAddingNewAddress) {
      setIsAddingNewAddress(true);
    }
  }, [isLoading, addresses.length, isAddingNewAddress, setIsAddingNewAddress]);

  const handleAddNew = () => {
    setIsAddingNewAddress(true);
  };

  const handleCancelForm = () => {
    setIsAddingNewAddress(false);
    setIsEditingDeliveryAddress(false);
  };

  const handleEditAddress = (addressId: string) => {
    setDeliveryAddress(addressId);
    setIsEditingDeliveryAddress(true);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner size="md" />
        </div>
      </div>
    );
  }

  return (
    <div
      id="checkout-delivery-address"
      className={cn(
        "border rounded-[12px] overflow-hidden transition-all",
        isFormOpen
          ? "border-primary/40 ring-1 ring-primary/15 dark:border-primary/50"
          : "border-border-secondary dark:border-gray-700"
      )}
    >
      <div className="bg-bg-secondary dark:bg-gray-800 border-b border-border-secondary dark:border-gray-700 flex items-center justify-between px-3 md:px-4 py-2.5 md:py-3">
        <h2 className="text-base md:text-lg lg:text-[20px] font-semibold leading-6 md:leading-7 lg:leading-[30px] text-text-primary dark:text-text-primary">
          {t("checkout.deliveryAddress")}
        </h2>
        {!isFormOpen && addresses.length > 0 && (
          <button
            onClick={handleAddNew}
            className="text-sm md:text-md lg:text-[14px] font-semibold text-text-brand hover:opacity-80 transition-colors flex items-center gap-1"
          >
            {t("checkout.addNewAddress")}
          </button>
        )}
      </div>

      <div className="p-3 md:p-4 space-y-3 md:space-y-4">
        {!isFormOpen && addresses.length > 0 && (
          <AddressPicker
            addresses={addresses}
            selectedAddressId={selectedDeliveryAddressId}
            onSelect={setDeliveryAddress}
            onEdit={handleEditAddress}
            onDelete={openDeleteAddressDialog}
          />
        )}

        {!isFormOpen && addresses.length === 0 && (
          <div className="flex flex-col items-center justify-center py-6 md:py-8 text-center">
            <MapPin className="w-10 h-10 md:w-12 md:h-12 text-text-tertiary dark:text-gray-400 mb-2 md:mb-3" />
            <p className="text-xs md:text-sm text-muted-foreground mb-3 md:mb-4">
              {t("checkout.noAddresses")}
            </p>
            <button
              onClick={handleAddNew}
              className="text-xs md:text-sm lg:text-[14px] font-semibold text-text-brand hover:opacity-80 transition-colors"
            >
              {t("checkout.addNewAddress")}
            </button>
          </div>
        )}

        {isFormOpen && (
          <AddressFormInline
            mode="delivery"
            editingAddress={isEditingDeliveryAddress ? selectedAddress : null}
            onCancel={handleCancelForm}
          />
        )}
      </div>
    </div>
  );
};
